import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { useNavigate } from "react-router-dom";
import { getStudentSubjects } from "../../redux/slices/subjectsSlice";
import { getSubjectBooks, setCurrentSubjectId } from "../../redux/slices/subjectBooksSlice";
import styles from "./Library.module.css";

function Library() {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux selectors
  const { studentSubjects, subjectsLoading } = useSelector((state) => state.subjects);
  const { subjectBooks, booksLoading, booksError, currentSubjectId } = useSelector((state) => state.subjectBooks);
  const { currentUser } = useSelector((state) => state.auth);
  
  // Local state
  const [selectedCategory, setSelectedCategory] = useState("الكل");
  const [searchQuery, setSearchQuery] = useState("");
  const [modalBook, setModalBook] = useState(null);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [selectedPDF, setSelectedPDF] = useState(null);

  // 1. Scroll to books section on search
  const booksSectionRef = React.useRef(null);

  // Check authentication and role
  const { isAuthenticated } = useSelector((state) => state.auth);
  const userRole = currentUser?.role || localStorage.getItem("userRole");
  const isStudent = userRole === "student";
  const token = localStorage.getItem('token3almny');

  // Redirect logic: غير مسجل → login, مسجل لكن ليس طالب → الرئيسية
  useEffect(() => {
    // إذا لم يكن مسجل دخول، اذهب لصفحة تسجيل الدخول
    if (!token && !isAuthenticated) {
      navigate("/login");
      return;
    }
    
    // إذا كان مسجل دخول لكن ليس طالب، اذهب للرئيسية
    if ((token || isAuthenticated) && userRole && !isStudent) {
      navigate("/");
      return;
    }
  }, [token, isAuthenticated, userRole, isStudent, navigate]);

  // Fetch student subjects on component mount
  useEffect(() => {
    if (isStudent) {
      dispatch(getStudentSubjects());
    }
  }, [dispatch, isStudent]);

  // Fetch books when category changes
  useEffect(() => {
    if (isStudent && studentSubjects.length > 0) {
      let subjectId = 0; // افتراضياً للكل
      
      if (selectedCategory !== "الكل") {
        // البحث عن المادة المطابقة للتصنيف
        const matchingSubject = studentSubjects.find(subject => 
          subject.title === selectedCategory
        );
        if (matchingSubject) {
          subjectId = matchingSubject.id;
        }
      }
      
      dispatch(setCurrentSubjectId(subjectId));
      dispatch(getSubjectBooks({ subjectId }));
    }
  }, [selectedCategory, studentSubjects, dispatch, isStudent]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setTimeout(() => {
      if (booksSectionRef.current) {
        booksSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Handle category change
  const handleCategoryChange = (categoryName) => {
    setSelectedCategory(categoryName);
    setSearchQuery(""); // Reset search when changing category
  };

  // Filter books based on search and category
  const filteredBooks = subjectBooks.filter(book => {
    const matchesCategory = selectedCategory === "الكل" || book.category === selectedCategory;
    const matchesSearch =
      searchQuery === "" ||
      book.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.author?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Modal close handler
  const closeModal = () => setModalBook(null);

  // دالة فتح PDF Viewer
  const openPDF = (pdfUrl, fileName) => {
    // Navigate to PDF viewer page with query parameters
    navigate(`/pdf-viewer?url=${encodeURIComponent(pdfUrl)}&name=${encodeURIComponent(fileName)}`);
  };

  // دالة فتح PDF في Google Viewer مباشرة
  const openPDFInGoogleViewer = (pdfUrl, fileName) => {
    const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(pdfUrl)}&embedded=true`;
    window.open(googleViewerUrl, '_blank');
  };

  // دالة إغلاق PDF Viewer (لم تعد مطلوبة)
  const closePDF = () => {
    setShowPDFViewer(false);
    setSelectedPDF(null);
  };

  // Get subject icon
  const getSubjectIcon = (subjectName) => {
    const iconMap = {
      "رياضيات": "🔢",
      "علوم": "🔬",
      "لغة عربية": "📖",
      "لغة إنجليزية": "🌍",
      "تاريخ": "📜",
      "جغرافيا": "🌍",
      "فيزياء": "⚡",
      "كيمياء": "🧪",
      "أحياء": "🧬"
    };
    return iconMap[subjectName] || "📚";
  };

  // Mock books for fallback (when API doesn't return data)
  const mockBooks = [
    {
      id: 1,
      title: "أساسيات الجبر الخطي",
      author: "د. أحمد محمد",
      category: "رياضيات",
      grade: "الصف الأول الثانوي",
      pages: 320,
      rating: 4.8,
      reviews: 156,
      downloads: 2847,
      cover: "📐",
      description: "كتاب شامل يغطي أساسيات الجبر الخطي والمصفوفات مع أمثلة عملية وتطبيقات واقعية",
      pdf: "/3almnyBook.pdf"
    },
    {
      id: 2,
      title: "الكيمياء العضوية المتقدمة",
      author: "د. فاطمة علي",
      category: "كيمياء",
      grade: "الصف الثاني الثانوي",
      pages: 450,
      rating: 4.9,
      reviews: 203,
      downloads: 3421,
      cover: "🧪",
      description: "دراسة شاملة للمركبات العضوية وتفاعلاتها مع التركيز على التطبيقات العملية",
      pdf: "/3almnyBook.pdf"
    },
    {
      id: 3,
      title: "قواعد اللغة العربية",
      author: "أ. سارة أحمد",
      category: "لغة عربية",
      grade: "الصف الأول الثانوي",
      pages: 280,
      rating: 4.7,
      reviews: 189,
      downloads: 2156,
      cover: "📖",
      description: "مرجع شامل لقواعد النحو والصرف مع أمثلة من القرآن الكريم والأدب العربي",
      pdf: "/3almnyBook.pdf"
    },
    {
      id: 4,
      title: "الفيزياء الكمية",
      author: "د. محمد حسن",
      category: "فيزياء",
      grade: "الصف الثالث الثانوي",
      pages: 380,
      rating: 4.6,
      reviews: 134,
      downloads: 1892,
      cover: "⚡",
      description: "مقدمة في ميكانيكا الكم ونظريات الفيزياء الحديثة مع التطبيقات العملية",
      pdf: "/3almnyBook.pdf"
    },
    {
      id: 5,
      title: "التاريخ الإسلامي",
      author: "د. خالد عبدالله",
      category: "تاريخ",
      grade: "الصف الثاني الثانوي",
      pages: 320,
      rating: 4.8,
      reviews: 167,
      downloads: 2341,
      cover: "📜",
      description: "تاريخ شامل للدولة الإسلامية من عهد الرسول صلى الله عليه وسلم حتى العصر الحديث",
      pdf: "/3almnyBook.pdf"
    },
    {
      id: 6,
      title: "جغرافيا العالم المعاصر",
      author: "د. نورا سعيد",
      category: "جغرافيا",
      grade: "الصف الأول الثانوي",
      pages: 290,
      rating: 4.5,
      reviews: 98,
      downloads: 1654,
      cover: "🌍",
      description: "دراسة شاملة لجغرافيا العالم مع التركيز على التحديات المعاصرة",
      pdf: "/3almnyBook.pdf"
    },
    {
      id: 7,
      title: "علم الأحياء الجزيئي",
      author: "د. أحمد إبراهيم",
      category: "أحياء",
      grade: "الصف الثالث الثانوي",
      pages: 410,
      rating: 4.9,
      reviews: 145,
      downloads: 1987,
      cover: "🧬",
      description: "دراسة متقدمة في علم الأحياء الجزيئي والهندسة الوراثية",
      pdf: "/3almnyBook.pdf"
    },
    {
      id: 8,
      title: "اللغة الإنجليزية المتقدمة",
      author: "أ. جيمس ويلسون",
      category: "لغة إنجليزية",
      grade: "الصف الثاني الثانوي",
      pages: 350,
      rating: 4.7,
      reviews: 178,
      downloads: 2678,
      cover: "🌍",
      description: "دورة متقدمة في اللغة الإنجليزية مع التركيز على المحادثة والكتابة الأكاديمية",
      pdf: "/3almnyBook.pdf"
    }
  ];

  // Use API books if available and not empty, otherwise use mock books
  const booksToDisplay = (subjectBooks.length > 0 && subjectBooks.some(book => book.title)) ? subjectBooks : mockBooks;
  
  // Debug logging
  console.log('API Books:', subjectBooks);
  console.log('Books to Display:', booksToDisplay);
  console.log('Selected Category:', selectedCategory);
  
  // Transform API books to match expected structure
  const transformedBooks = booksToDisplay.map(book => {
    if (book.file_url) {
      // This is an API book - transform it
      return {
        ...book,
        id: book.id,
        title: book.title,
        author: book.from_whom || "غير محدد",
        category: book.title, // Use title as category
        grade: "الصف الثانوي", // Default grade
        pages: 50, // Default pages + 10
        rating: 4.5, // Fixed rating
        reviews: 25, // Default reviews
        downloads: 150, // Default downloads
        cover: getSubjectIcon(book.title), // Use subject icon
        description: book.description || "لا يوجد وصف متاح",
        pdf: book.file_url // Use file_url as pdf
      };
    } else {
      // This is a mock book - keep as is
      return book;
    }
  });
  
  const finalFilteredBooks = transformedBooks.filter(book => {
    const matchesCategory = selectedCategory === "الكل" || book.category === selectedCategory;
    const matchesSearch =
      searchQuery === "" ||
      book.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.author?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  console.log('Transformed Books:', transformedBooks);
  console.log('Final Filtered Books:', finalFilteredBooks);

  // Animation effects
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            
            // Start counter animation when stats section becomes visible
            if (entry.target.classList.contains(styles['library-stats'])) {
              const counters = entry.target.querySelectorAll('.counter');
              counters.forEach((counter, index) => {
                const target = parseFloat(counter.getAttribute('data-target'));
                const isDecimal = counter.hasAttribute('data-decimal');
                const duration = 2000; // 2 seconds
                const startTime = Date.now();
                
                const updateCounter = () => {
                  const elapsed = Date.now() - startTime;
                  const progress = Math.min(elapsed / duration, 1);
                  
                  // Easing function for smooth animation
                  const easeOutQuart = 1 - Math.pow(1 - progress, 4);
                  const current = target * easeOutQuart;
                  
                  if (progress < 1) {
                    if (isDecimal) {
                      counter.textContent = (current || 0).toFixed(1);
                    } else {
                      counter.textContent = Math.floor(current || 0).toLocaleString();
                    }
                    requestAnimationFrame(updateCounter);
                  } else {
                    if (isDecimal) {
                      counter.textContent = (target || 0).toFixed(1);
                    } else {
                      counter.textContent = (target || 0).toLocaleString();
                    }
                    // Add pulse animation when counter reaches target
                    counter.classList.add('animate');
                    setTimeout(() => counter.classList.remove('animate'), 500);
                  }
                };
                
                // Delay each counter slightly for staggered effect
                setTimeout(updateCounter, index * 200);
              });
            }
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // If not student, don't render
  if (!isStudent) {
    return null;
  }

  return (
    <div className={`${styles['library-page']} ${isDarkMode ? 'dark-mode' : 'light-mode'} ${styles['library-container']}`}>
      {/* Floating Background Elements */}
      <div className={styles['floating-elements']}>
        <div className={styles['floating-element']}>📚</div>
        <div className={styles['floating-element']}>🔬</div>
        <div className={styles['floating-element']}>📖</div>
        <div className={styles['floating-element']}>🧪</div>
        <div className={styles['floating-element']}>⚡</div>
        <div className={styles['floating-element']}>🔢</div>
        <div className={styles['floating-element']}>📜</div>
        <div className={styles['floating-element']}>🌍</div>
      </div>

      <div className={`${styles['container']} container`}>
        {/* Header */}
        <div className={`${styles['library-header']} animate-on-scroll`}>
          <h1 className={styles['library-title']}>مكتبة علمني</h1>
          <p className={styles['library-subtitle']}>اكتشف آلاف الكتب العلمية والمراجع التعليمية</p>
          {/* Search Bar */}
          <div className={styles['search-container']}>
            <div className={styles['search-box']}>
              <input
                type="text"
                placeholder="ابحث عن كتاب أو مؤلف..."
                value={searchQuery}
                onChange={handleSearchChange}
                className={styles['search-input']}
              />
              <button className={styles['search-btn']} onClick={e => e.preventDefault()}>🔍</button>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className={`${styles['library-stats']} animate-on-scroll`}>
          <div className={styles['stat-item']}>
            <div className={`${styles['stat-number']} counter`} data-target="2847">0</div>
            <div className={styles['stat-label']}>كتاب علمي</div>
          </div>
          <div className={styles['stat-item']}>
            <div className={`${styles['stat-number']} counter`} data-target="156">0</div>
            <div className={styles['stat-label']}>مؤلف متخصص</div>
          </div>
          <div className={styles['stat-item']}>
            <div className={`${styles['stat-number']} counter`} data-target="45892">0</div>
            <div className={styles['stat-label']}>تحميل هذا الشهر</div>
          </div>
          <div className={styles['stat-item']}>
            <div className={`${styles['stat-number']} counter`} data-target="4.8" data-decimal="true">0</div>
            <div className={styles['stat-label']}>تقييم متوسط</div>
          </div>
        </div>

        {/* Categories - Now showing subjects from API */}
        <div className={`${styles['categories-section']} animate-on-scroll`}>
          <h2 className={styles['section-title']}>المواد الدراسية</h2>
          <div className={styles['categories-grid']}>
            {/* خيار الكل */}
            <button
              className={`${styles['category-btn']} ${selectedCategory === "الكل" ? styles['active'] : ''}`}
              onClick={() => handleCategoryChange("الكل")}
            >
              <span className={styles['category-icon']}>📚</span>
              <span className={styles['category-name']}>الكل</span>
            </button>
            
            {/* المواد من API */}
            {!subjectsLoading && studentSubjects && studentSubjects.map((subject) => (
              <button
                key={subject.id}
                className={`${styles['category-btn']} ${selectedCategory === subject.title ? styles['active'] : ''}`}
                onClick={() => handleCategoryChange(subject.title)}
              >
                <span className={styles['category-icon']}>{getSubjectIcon(subject.title)}</span>
                <span className={styles['category-name']}>{subject.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Books Grid */}
        <div className={`${styles['books-section']} `} ref={booksSectionRef}>
          <h2 className={styles['section-title']}>
            {selectedCategory === "الكل" ? "جميع الكتب" : `كتب ${selectedCategory}`}
            <span className={styles['books-count']}>({finalFilteredBooks.length} كتاب)</span>
          </h2>
          
          {/* Loading State */}
          {booksLoading && (
            <div className="text-center my-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {booksError && (
            <div className="alert alert-danger text-center" role="alert">
              {booksError}
            </div>
          )}

          {/* Books Grid */}
          {!booksLoading && !booksError && finalFilteredBooks.length > 0 && (
          <div className={styles['books-grid']}>
              {finalFilteredBooks.map((book, index) => (
              <div 
                key={book.id} 
                className={styles['book-card']}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={styles['book-cover']}>
                    <div className={styles['book-icon']}>{book.cover || "📚"}</div>
                  <div className={styles['book-overlay']}>
                    <a
                      className={styles['download-btn']}
                        href={book.pdf || book.file_url}
                      download={book.title + ".pdf"}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{textDecoration : "none"}}
                    >⬇️ تحميل</a>
                    <button
                      className={styles['preview-btn']}
                        onClick={() => openPDF(book.pdf || book.file_url, book.title)}
                      style={{textDecoration : "none"}}
                    >👁️ عرض</button>
                     
                  </div>
                </div>
                <div className={styles['book-info']}>
                  <h3 className={styles['book-title']}>{book.title}</h3>
                    <p className={styles['book-author']}>تأليف: {book.author || "غير محدد"}</p>
                  <div className={styles['book-meta']}>
                      <span className={styles['book-category']}>{book.category || book.subject_name}</span>
                      <span className={styles['book-grade']}>{book.grade || book.level || "غير محدد"}</span>
                  </div>
                    <p className={styles['book-description']}>{book.description || "لا يوجد وصف متاح"}</p>
                  <div className={styles['book-stats']}>
                    <div className={styles['book-rating']}>
                        <span className={styles['stars']}>{"★".repeat(Math.floor(book.rating || 0))}</span>
                        <span className={styles['rating-number']}>{book.rating || 0}</span>
                        <span className={styles['reviews']}>({book.reviews || 0})</span>
                    </div>
                    <div className={styles['book-downloads']}>
                      <span className={styles['downloads-icon']}>⬇️</span>
                        <span className={styles['downloads-number']}>{book.downloads || 0}</span>
                    </div>
                  </div>
                  <div className={styles['book-pages']}>
                    <span className={styles['pages-icon']}>📄</span>
                      <span className={styles['pages-number']}>{book.pages || 0} صفحة</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Books Found */}
          {!booksLoading && !booksError && finalFilteredBooks.length === 0 && (
            <div className="text-center my-5">
              <div className="alert alert-info" role="alert">
                <h4>لا توجد كتب متاحة</h4>
                <p>جرب تغيير المادة أو البحث بكلمات مختلفة</p>
              </div>
          </div>
          )}
        </div>

        {/* Featured Section */}
        {!booksLoading && !booksError && booksToDisplay.length > 0 && (
        <div className={`${styles['featured-section']} animate-on-scroll`}>
          <h2 className={styles['section-title']}>كتب مميزة هذا الأسبوع</h2>
          <div className={styles['featured-books']}>
              {booksToDisplay.slice(0, 3).map((book, index) => (
              <div key={book.id} className={styles['featured-book']}>
                <div className={styles['featured-cover']}>
                    <div className={styles['featured-icon']}>{book.cover || "📚"}</div>
                  <div className={styles['featured-badge']}>🔥 مميز</div>
                </div>
                <div className={styles['featured-info']}>
                  <h3>{book.title}</h3>
                    <p>{book.author || "غير محدد"}</p>
                  <div className={styles['featured-rating']}>
                      {"★".repeat(Math.floor(book.rating || 0))} {book.rating || 0}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>

      {/* Book Preview Modal */}
      {modalBook && (
        <div className={styles['library-modal-overlay']} onClick={closeModal}>
          <div className={styles['library-modal']} onClick={e => e.stopPropagation()}>
            <button className={styles['close-modal-btn']} onClick={closeModal}>×</button>
            <div className={styles['modal-book-icon']}>{modalBook.cover || "📚"}</div>
            <h2 className={styles['modal-book-title']}>{modalBook.title}</h2>
            <div className={styles['modal-book-author']}>تأليف: {modalBook.author || "غير محدد"}</div>
            <div className={styles['modal-book-meta']}>
              <span>{modalBook.category || modalBook.subject_name}</span> | <span>{modalBook.grade || modalBook.level || "غير محدد"}</span>
            </div>
            <div className={styles['modal-book-pages']}>عدد الصفحات: {modalBook.pages || 0}</div>
            <div className={styles['modal-book-description']}>{modalBook.description || "لا يوجد وصف متاح"}</div>
            <div className={`${styles['modal-book-actions']} mt-3`}>
              <a
                className={styles['download-btn']}
                href={modalBook.pdf || modalBook.file_url}
                download={modalBook.title + ".pdf"}
                target="_blank"
                rel="noopener noreferrer"
              >⬇️ تحميل الكتاب</a>
              <a
                className={styles['preview-btn']}
                href={modalBook.pdf || modalBook.file_url}
                target="_blank"
                rel="noopener noreferrer"
              >👁️ فتح نسخة PDF</a>
            </div>
          </div>
        </div>
      )}

      {/* SVG للخطوط المتصلة بين الكتب */}
      <div className={styles['books-constellation-container']}>
        <svg className={styles['books-constellation-bg']} width="100%" height="100%">
          {finalFilteredBooks.map((book, i) => {
            if (i === 0) return null;
            // خطوط بين كل كتاب والذي قبله
            const x1 = 200 + 180 * Math.cos((2 * Math.PI * (i-1)) / finalFilteredBooks.length);
            const y1 = 200 + 180 * Math.sin((2 * Math.PI * (i-1)) / finalFilteredBooks.length);
            const x2 = 200 + 180 * Math.cos((2 * Math.PI * i) / finalFilteredBooks.length);
            const y2 = 200 + 180 * Math.sin((2 * Math.PI * i) / finalFilteredBooks.length);
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#017FD2" strokeWidth="2" opacity="0.3" strokeDasharray="8 4">
              <animate attributeName="stroke-dashoffset" values="0;12;0" dur="2s" repeatCount="indefinite" />
            </line>;
          })}
        </svg>
      </div>
    </div>
  );
}

export default Library; 